import React, { useState } from 'react';
import { theme, alpha } from '../../../theme';
import {
  X,
  User,
  Phone,
  Clock,
  Calendar,
  DollarSign,
  FileText,
  Check,
  XCircle,
  Edit2,
  Save,
  ChevronDown,
  ChevronUp,
  Mail,
} from 'lucide-react';
import { GridBooking, QuickEditBookingRequest, BOOKING_COLORS, DAY_OF_WEEK_LABELS } from '../../../types/venue';

interface BookingDetailModalProps {
  isOpen: boolean;
  booking: GridBooking | null;
  onClose: () => void;
  onQuickEdit: (
    bookingId: string,
    data: QuickEditBookingRequest
  ) => Promise<{ success: boolean; error?: string }>;
  onCancel: (bookingId: string) => Promise<{ success: boolean; error?: string }>;
}

const BookingDetailModal: React.FC<BookingDetailModalProps> = ({
  isOpen,
  booking,
  onClose,
  onQuickEdit,
  onCancel,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<QuickEditBookingRequest>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    info: true,
    organizer: true,
    payment: true,
    notes: false,
  });

  if (!isOpen || !booking) return null;

  const toggleSection = (key: string) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleEdit = () => {
    setEditData({
      customerName: booking.customerName || booking.booker?.name || '',
      customerPhone: booking.customerPhone || '',
      notes: booking.notes || '',
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    const result = await onQuickEdit(booking.id, editData);
    setLoading(false);
    if (result.success) {
      setIsEditing(false);
    } else {
      setError(result.error || 'Error al guardar cambios');
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('¿Estás seguro de cancelar esta reserva?')) return;
    setLoading(true);
    setError(null);
    const result = await onCancel(booking.id);
    setLoading(false);
    if (result.success) {
      onClose();
    } else {
      setError(result.error || 'Error al cancelar reserva');
    }
  };

  const handleMarkCompleted = async () => {
    setLoading(true);
    setError(null);
    const result = await onQuickEdit(booking.id, { status: 'COMPLETED' });
    setLoading(false);
    if (!result.success) {
      setError(result.error || 'Error al completar reserva');
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return `${DAY_OF_WEEK_LABELS[date.getDay()]} ${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}`;
  };

  const getStatusLabel = () => {
    switch (booking.status) {
      case 'PENDING': return { label: 'PENDIENTE', color: '#FF9800' };
      case 'CONFIRMED': return { label: 'CONFIRMADA', color: theme.colors.primary };
      case 'COMPLETED': return { label: 'FINALIZADO', color: '#4CAF50' };
      case 'CANCELLED': return { label: 'CANCELADA', color: theme.colors.danger };
      case 'NO_SHOW': return { label: 'NO ASISTIO', color: theme.colors.secondary };
      default: return { label: booking.status, color: theme.colors.textSecondary };
    }
  };

  const getTypeLabel = () => {
    switch (booking.bookingType) {
      case 'ONLINE': return 'Online';
      case 'FIXED': return 'Turno Fijo';
      case 'MANUAL': return 'Manual';
      default: return booking.bookingType;
    }
  };

  const status = getStatusLabel();

  // Side panel styles
  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000,
  };

  const panelStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    width: '520px',
    maxWidth: '100vw',
    backgroundColor: theme.colors.surface,
    boxShadow: '-4px 0 24px rgba(0,0,0,0.3)',
    zIndex: 1001,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };

  const headerStyle: React.CSSProperties = {
    padding: '20px 24px',
    borderBottom: `1px solid ${theme.colors.border}`,
    backgroundColor: theme.colors.background,
  };

  const headerTopRow: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '1.3rem',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    fontFamily: theme.fonts.heading,
  };

  const badgeRowStyle: React.CSSProperties = {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  };

  const statusBadge: React.CSSProperties = {
    padding: '3px 10px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: theme.fonts.weightBold,
    backgroundColor: status.color,
    color: '#fff',
    letterSpacing: '0.5px',
  };

  const closeBtn: React.CSSProperties = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '6px',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const bodyStyle: React.CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    padding: '0',
  };

  const sectionHeaderStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 24px',
    cursor: 'pointer',
    backgroundColor: theme.colors.background,
    borderBottom: `1px solid ${theme.colors.border}`,
    userSelect: 'none',
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeBody,
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
  };

  const sectionContentStyle: React.CSSProperties = {
    padding: '16px 24px',
    borderBottom: `1px solid ${theme.colors.border}`,
  };

  const fieldRow: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 0',
  };

  const fieldLabel: React.CSSProperties = {
    fontSize: '12px',
    color: theme.colors.textSecondary,
    minWidth: '80px',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  };

  const fieldValue: React.CSSProperties = {
    fontSize: theme.fonts.sizeBody,
    color: theme.colors.text,
    fontWeight: theme.fonts.weightBold,
    flex: 1,
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    borderRadius: theme.borderRadius.input,
    border: `1px solid ${theme.colors.border}`,
    backgroundColor: theme.colors.background,
    color: theme.colors.text,
    fontSize: theme.fonts.sizeSmall,
    flex: 1,
  };

  const paymentRowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: `1px solid ${theme.colors.border}`,
  };

  const totalRowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0 4px',
    fontWeight: theme.fonts.weightBold,
    fontSize: theme.fonts.sizeSubtitle,
  };

  const pendingStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0 0',
    color: theme.colors.danger,
    fontWeight: theme.fonts.weightBold,
  };

  const errorBoxStyle: React.CSSProperties = {
    backgroundColor: `${alpha('danger', '20')}`,
    color: theme.colors.danger,
    padding: '12px 16px',
    borderRadius: theme.borderRadius.input,
    fontSize: theme.fonts.sizeSmall,
    margin: '16px 24px',
  };

  const footerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '16px 24px',
    borderTop: `1px solid ${theme.colors.border}`,
    backgroundColor: theme.colors.background,
  };

  const btnBase: React.CSSProperties = {
    padding: '10px 20px',
    borderRadius: theme.borderRadius.button,
    fontSize: theme.fonts.sizeSmall,
    fontWeight: theme.fonts.weightBold,
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  };

  const cancelBtnStyle: React.CSSProperties = {
    ...btnBase,
    backgroundColor: `${alpha('danger', '15')}`,
    border: `1px solid ${theme.colors.danger}`,
    color: theme.colors.danger,
  };

  const completeBtnStyle: React.CSSProperties = {
    ...btnBase,
    backgroundColor: theme.colors.primary,
    border: 'none',
    color: '#000',
  };

  const editBtnStyle: React.CSSProperties = {
    ...btnBase,
    backgroundColor: 'transparent',
    border: `1px solid ${theme.colors.border}`,
    color: theme.colors.text,
  };

  const saveBtnStyle: React.CSSProperties = {
    ...btnBase,
    backgroundColor: theme.colors.primary,
    border: 'none',
    color: '#000',
  };

  return (
    <>
      <div style={overlayStyle} onClick={onClose} />
      <div style={panelStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={headerTopRow}>
            <div style={titleStyle}>
              Turno {formatDate(booking.bookingDate)} - {booking.startTime}hs
            </div>
            <button style={closeBtn} onClick={onClose}>
              <X size={22} color={theme.colors.textSecondary} />
            </button>
          </div>
          <div style={badgeRowStyle}>
            <span style={statusBadge}>{status.label}</span>
            <span style={{
              ...statusBadge,
              backgroundColor: `${alpha('primary', '20')}`,
              color: theme.colors.primary,
            }}>
              {getTypeLabel()}
            </span>
          </div>
        </div>

        {/* Body */}
        <div style={bodyStyle}>
          {error && <div style={errorBoxStyle}>{error}</div>}

          {/* Info del turno */}
          <div style={sectionHeaderStyle} onClick={() => toggleSection('info')}>
            <span style={sectionTitleStyle}>Informacion del turno</span>
            {expandedSections.info ? <ChevronUp size={18} color={theme.colors.textSecondary} /> : <ChevronDown size={18} color={theme.colors.textSecondary} />}
          </div>
          {expandedSections.info && (
            <div style={sectionContentStyle}>
              <div style={fieldRow}>
                <Calendar size={16} color={theme.colors.textSecondary} />
                <span style={fieldLabel}>Fecha</span>
                <span style={fieldValue}>{formatDate(booking.bookingDate)}</span>
              </div>
              <div style={fieldRow}>
                <Clock size={16} color={theme.colors.textSecondary} />
                <span style={fieldLabel}>Horario</span>
                <span style={fieldValue}>{booking.startTime} - {booking.endTime}</span>
              </div>
            </div>
          )}

          {/* Organizador */}
          <div style={sectionHeaderStyle} onClick={() => toggleSection('organizer')}>
            <span style={sectionTitleStyle}>Organizador</span>
            {expandedSections.organizer ? <ChevronUp size={18} color={theme.colors.textSecondary} /> : <ChevronDown size={18} color={theme.colors.textSecondary} />}
          </div>
          {expandedSections.organizer && (
            <div style={sectionContentStyle}>
              <div style={fieldRow}>
                <User size={16} color={theme.colors.textSecondary} />
                <span style={fieldLabel}>Nombre</span>
                {isEditing ? (
                  <input
                    style={inputStyle}
                    value={editData.customerName || ''}
                    onChange={(e) => setEditData({ ...editData, customerName: e.target.value })}
                  />
                ) : (
                  <span style={fieldValue}>{booking.customerName || booking.booker?.name || '-'}</span>
                )}
              </div>
              <div style={fieldRow}>
                <Phone size={16} color={theme.colors.textSecondary} />
                <span style={fieldLabel}>Telefono</span>
                {isEditing ? (
                  <input
                    style={inputStyle}
                    value={editData.customerPhone || ''}
                    onChange={(e) => setEditData({ ...editData, customerPhone: e.target.value })}
                  />
                ) : (
                  <span style={fieldValue}>{booking.customerPhone || '-'}</span>
                )}
              </div>
              <div style={fieldRow}>
                <Mail size={16} color={theme.colors.textSecondary} />
                <span style={fieldLabel}>Email</span>
                <span style={fieldValue}>{booking.booker?.email || '-'}</span>
              </div>
            </div>
          )}

          {/* Cobros */}
          <div style={sectionHeaderStyle} onClick={() => toggleSection('payment')}>
            <span style={sectionTitleStyle}>Cobros del turno</span>
            {expandedSections.payment ? <ChevronUp size={18} color={theme.colors.textSecondary} /> : <ChevronDown size={18} color={theme.colors.textSecondary} />}
          </div>
          {expandedSections.payment && (
            <div style={sectionContentStyle}>
              <div style={paymentRowStyle}>
                <span style={{ color: theme.colors.textSecondary, fontSize: '13px' }}>TURNO</span>
                <span style={{ fontWeight: theme.fonts.weightBold, color: theme.colors.text }}>
                  ${booking.totalAmount?.toFixed(0) || '0'}
                </span>
              </div>
              <div style={totalRowStyle}>
                <span style={{ color: theme.colors.text }}>TOTAL</span>
                <span style={{ color: theme.colors.text }}>${booking.totalAmount?.toFixed(0) || '0'}</span>
              </div>
              <div style={pendingStyle}>
                <span>Saldo pendiente</span>
                <span>${booking.totalAmount?.toFixed(0) || '0'}</span>
              </div>
            </div>
          )}

          {/* Notas */}
          <div style={sectionHeaderStyle} onClick={() => toggleSection('notes')}>
            <span style={sectionTitleStyle}>Notas</span>
            {expandedSections.notes ? <ChevronUp size={18} color={theme.colors.textSecondary} /> : <ChevronDown size={18} color={theme.colors.textSecondary} />}
          </div>
          {expandedSections.notes && (
            <div style={sectionContentStyle}>
              {isEditing ? (
                <textarea
                  style={{ ...inputStyle, minHeight: '80px', resize: 'vertical', width: '100%' }}
                  value={editData.notes || ''}
                  onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                  placeholder="Observaciones..."
                />
              ) : (
                <span style={{ color: theme.colors.textSecondary, fontSize: '13px' }}>
                  {booking.notes || 'Sin notas'}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <div>
            {booking.status !== 'CANCELLED' && booking.status !== 'COMPLETED' && (
              <button style={cancelBtnStyle} onClick={handleCancel} disabled={loading}>
                <XCircle size={16} />
                Cancelar turno
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {isEditing ? (
              <>
                <button style={editBtnStyle} onClick={() => setIsEditing(false)} disabled={loading}>
                  Descartar
                </button>
                <button style={saveBtnStyle} onClick={handleSave} disabled={loading}>
                  <Save size={16} />
                  {loading ? 'Guardando...' : 'Guardar'}
                </button>
              </>
            ) : (
              <>
                <button style={editBtnStyle} onClick={handleEdit} disabled={loading || booking.status === 'CANCELLED'}>
                  <Edit2 size={16} />
                  Editar
                </button>
                {booking.status === 'CONFIRMED' && (
                  <button style={completeBtnStyle} onClick={handleMarkCompleted} disabled={loading}>
                    <Check size={16} />
                    Finalizar turno
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default BookingDetailModal;
